import {
  Step as ModelStep,
  RedoManagedEditableField,
  ReturnFlow,
  StepFn,
  stepsBuildWithMapping,
} from "@redotech/redo-return-core-common/return-flow";
import { FieldId } from "@redotech/redo-return-core-common/return-flow-answers";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import type {
  AddExchangeOrderTagsState,
  AddReturnTagState,
  AddUndeclaredItemState,
  BlockState,
  CombineLabelState,
  DiscountCodeNameState,
  DonateState,
  DoNothingState,
  DynamicBundleAdjustmentState,
  EvidenceState,
  ExchangeShippingNameState,
  ExchangeShippingRatesState,
  FinalSaleReturnState,
  FlagState,
  InformationState,
  InputState,
  MakeServiceRequestState,
  ManualReviewState,
  MerchantReviewBeforeFilingState,
  OverrideAdjustmentState,
  OverrideGreenReturnState,
  ProcessingActionState,
  RedoManagedLocationState,
  RejectState,
  ReturnProcessingDecisionState,
  ReturnProcessingInformationState,
  ReturnState,
  SendEmailState,
  SendSmsState,
  SeparateLabelsState,
  SetReturnLocationsState,
  SortState,
  SubmitWarrantyRegistrationState,
  TransformProductState,
  TriggerState,
  VerificationState,
  VerifyItemVariantState,
  WaitState,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
import { Condition } from "@redotech/redo-return-core-common/return-flow/condition";
import { returnStepTypeName } from "@redotech/redo-return-core-common/return-flow/step-type-names";
import * as React from "react";
import {
  ADD_CONVERSATION_TAG,
  AddConversationTagState,
} from "./chat-flow/add-conversation-tag";
import {
  ASSIGN_TO_AGENT,
  AssignToAgentState,
} from "./chat-flow/assign-to-agent";
import {
  CHAT_MULTIPLE_CHOICE,
  ChatMultipleChoiceState,
} from "./chat-flow/chat-multiple-choice";
import { CLOSE_CHAT, CloseChatState } from "./chat-flow/close-chat";
import {
  CUSTOMER_RESPONSE,
  CustomerResponseState,
} from "./chat-flow/customer-response";
import { REENTRY, ReentryState } from "./chat-flow/reentry";
import { SEE_ORDERS, SeeOrdersState } from "./chat-flow/see-orders";
import { SEND_MESSAGE, SendMessageState } from "./chat-flow/send-message";
import { AB_TEST, AbTestState } from "./return-flow/ab-test";
import { ADD_EXCHANGE_ORDER_TAGS } from "./return-flow/add-exchange-order-tags";
import { ADD_RETURN_TAG, getTagTypeName } from "./return-flow/add-return-tag";
import { ADD_UNDECLARED_ITEM } from "./return-flow/add-undeclared-item";
import {
  AI_RETURN_REASONS,
  AiReturnReasonsState,
  aiReturnReasonsStepOptions,
} from "./return-flow/ai-return-reasons";
import { BLOCK } from "./return-flow/block";
import { COMBINE_LABEL } from "./return-flow/combine-label";
import {
  COMPENSATION,
  COMPENSATION_STEP,
  CompensationState,
  CompensationStepModel,
} from "./return-flow/compensation";
import {
  CONDITION,
  ConditionState,
  ConditionTypeDetailsProps,
} from "./return-flow/condition";
import { DISCOUNT_CODE_NAME } from "./return-flow/discount-code-name";
import { DO_NOTHING } from "./return-flow/do-nothing";
import { DYNAMIC_BUNDLE_ADJUSTMENT } from "./return-flow/dynamic-bundle-adjustment";
import {
  END_NODE,
  END_NODE_STEP,
  EndNodeState,
  EndNodeStepModel,
} from "./return-flow/end-node";
import { EVIDENCE } from "./return-flow/evidence";
import { EXCHANGE_SHIPPING_NAME } from "./return-flow/exchange-shipping-name";
import { EXCHANGE_SHIPPING_RATES } from "./return-flow/exchange-shipping-rates";
import { FINAL_SALE_RETURN } from "./return-flow/final-sale";
import { FLAG } from "./return-flow/flag";
import { INFORMATION } from "./return-flow/information";
import { INPUT } from "./return-flow/input";
import { MAKE_SERVICE_REQUEST } from "./return-flow/make-service-request";
import { MANUAL_REVIEW } from "./return-flow/manual-review";
import { MERCHANT_REVIEW_BEFORE_FILING } from "./return-flow/merchant-review-before-filing";
import {
  getReturnReasonStep,
  MULTIPLE_CHOICE,
  MultipleChoiceState,
} from "./return-flow/multiple-choice";
import { ORDER_DATE_INPUT } from "./return-flow/order-date-input";
import { OVERRIDE_ADJUSTMENT } from "./return-flow/override-adjustment";
import { OVERRIDE_GREEN_RETURN } from "./return-flow/override-green-return";
import {
  PHASE_ANCHOR,
  PHASE_ANCHOR_STEP,
  PhaseAnchorState,
  PhaseAnchorStepModel,
} from "./return-flow/phase-anchor";
import { PROCESSING_ACTION } from "./return-flow/processing-action";
import { REDO_MANAGED_LOCATION } from "./return-flow/redo-managed-location";
import { REJECT } from "./return-flow/reject";
import {
  RESTOCKING,
  RESTOCKING_STEP,
  RestockingState,
  RestockingStepModel,
} from "./return-flow/restocking";
import { RETURN } from "./return-flow/return";
import { RETURN_PROCESSING_DECISION } from "./return-flow/return-processing-decision";
import { RETURN_PROCESSING_INFORMATION } from "./return-flow/return-processing-information";
import { SEND_EMAIL } from "./return-flow/send-email";
import { SEND_SMS } from "./return-flow/send-sms";
import { SEPARATE_LABELS } from "./return-flow/separate-labels";
import {
  DONATE,
  SET_RETURN_LOCATIONS,
} from "./return-flow/set-return-locations";
import { SORT } from "./return-flow/sort";
import { SUBMIT_CLAIM, SubmitClaimState } from "./return-flow/submit-claim";
import { SUBMIT_REPAIR, SubmitRepairState } from "./return-flow/submit-repair";
import {
  SUBMIT_WARRANTY,
  SubmitWarrantyState,
} from "./return-flow/submit-warranty";
import { SUBMIT_WARRANTY_REGISTRATION } from "./return-flow/submit-warranty-registration";
import { TRANSFORM_PRODUCT } from "./return-flow/transform-product";
import { TRIGGER } from "./return-flow/trigger";
import { VERIFICATION } from "./return-flow/verification";
import { VERIFY_ITEM_VARIANT } from "./return-flow/verify-item-variant";
import { WAIT } from "./return-flow/wait";
import {
  DetailsComponentsForActions,
  MULTIPLE_ACTIONS,
  MultipleActionsState,
} from "./rules/multiple-actions";
import {
  DetailsComponentsForRuleConditions,
  MULTIPLE_CONDITIONS,
  MultipleConditionsState,
} from "./rules/multiple-conditions";
import { getStartStep, StartState } from "./start";
import {
  Step,
  StepId,
  StepType,
  StepTypeDetailsProps,
  TypedStep,
} from "./step";

function invalidStepType(s: never): never {
  throw new Error("Invalid step type");
}

/**
 * Step types accepted by `modelToSteps`. Extends the `ReturnFlow` `Step` union
 * with editor-internal compensation/restocking step models so the
 * AdvancedFlowEditorShell can translate `AdvancedFlow` steps into something
 * the new flow editor understands without round-tripping through
 * `redo/model/src/return-flow.ts`.
 */
export type ExtendedModelStep =
  | ModelStep
  | CompensationStepModel
  | RestockingStepModel
  | PhaseAnchorStepModel
  | EndNodeStepModel;

export type ExtendedReturnFlow = Omit<ReturnFlow, "steps"> & {
  steps: ExtendedModelStep[];
};

export interface RedoManagedStepMeta {
  editableFields?: RedoManagedEditableField[];
  editableHandles?: number[];
}

/**
 * Redo-managed marker for each step, keyed by the same editor id `modelToSteps`
 * assigns (`String(index)`), so it round-trips without living on `TypedStep`.
 */
export function collectRedoManagedMeta(
  model: ExtendedReturnFlow,
): Map<StepId, RedoManagedStepMeta> {
  const meta = new Map<StepId, RedoManagedStepMeta>();
  for (const [index, step] of model.steps.entries()) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    if ((step as ModelStep).redoManaged) {
      meta.set(String(index), {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        editableFields: (step as ModelStep).redoManagedEditableFields,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        editableHandles: (step as ModelStep).redoManagedEditableHandles,
      });
    }
  }
  return meta;
}

export interface DetailsComponentsForSteps {
  Start: React.ComponentType<StepTypeDetailsProps<StartState>>;
  MultipleChoice: React.ComponentType<
    StepTypeDetailsProps<MultipleChoiceState>
  >;
  Flag: React.ComponentType<StepTypeDetailsProps<FlagState>>;
  ManualReview: React.ComponentType<StepTypeDetailsProps<ManualReviewState>>;
  MerchantReviewBeforeFiling: React.ComponentType<
    StepTypeDetailsProps<MerchantReviewBeforeFilingState>
  >;
  DoNothing: React.ComponentType<StepTypeDetailsProps<DoNothingState>>;
  SubmitWarrantyRegistration: React.ComponentType<
    StepTypeDetailsProps<SubmitWarrantyRegistrationState>
  >;
  Input: React.ComponentType<StepTypeDetailsProps<InputState>>;
  Information: React.ComponentType<StepTypeDetailsProps<InformationState>>;
  ExchangeShippingName: React.ComponentType<
    StepTypeDetailsProps<ExchangeShippingNameState>
  >;
  ExchangeShippingRates: React.ComponentType<
    StepTypeDetailsProps<ExchangeShippingRatesState>
  >;
  DiscountCodeName: React.ComponentType<
    StepTypeDetailsProps<DiscountCodeNameState>
  >;
  Reject: React.ComponentType<StepTypeDetailsProps<RejectState>>;
  OverrideAdjustment: React.ComponentType<
    StepTypeDetailsProps<OverrideAdjustmentState>
  >;
  OverrideGreenReturn: React.ComponentType<
    StepTypeDetailsProps<OverrideGreenReturnState>
  >;
  Return: React.ComponentType<StepTypeDetailsProps<ReturnState>>;
  FinalSaleReturn: React.ComponentType<
    StepTypeDetailsProps<FinalSaleReturnState>
  >;
  SubmitClaim: React.ComponentType<StepTypeDetailsProps<SubmitClaimState>>;
  SubmitWarranty: React.ComponentType<
    StepTypeDetailsProps<SubmitWarrantyState>
  >;
  SubmitRepair: React.ComponentType<StepTypeDetailsProps<SubmitRepairState>>;
  Block: React.ComponentType<StepTypeDetailsProps<BlockState>>;
  ChatMultipleChoice: React.ComponentType<
    StepTypeDetailsProps<ChatMultipleChoiceState>
  >;
  CloseChat: React.ComponentType<StepTypeDetailsProps<CloseChatState>>;
  CustomerResponse: React.ComponentType<
    StepTypeDetailsProps<CustomerResponseState>
  >;
  AssignToAgent: React.ComponentType<StepTypeDetailsProps<AssignToAgentState>>;
  SendMessage: React.ComponentType<StepTypeDetailsProps<SendMessageState>>;
  Trigger: React.ComponentType<StepTypeDetailsProps<TriggerState>>;
  Wait: React.ComponentType<StepTypeDetailsProps<WaitState>>;
  SendEmail: React.ComponentType<StepTypeDetailsProps<SendEmailState>>;
  SendSms: React.ComponentType<StepTypeDetailsProps<SendSmsState>>;
  SeeOrders: React.ComponentType<StepTypeDetailsProps<SeeOrdersState>>;
  AddConversationTag: React.ComponentType<
    StepTypeDetailsProps<AddConversationTagState>
  >;
  Reentry: React.ComponentType<StepTypeDetailsProps<ReentryState>>;
  AbTest: React.ComponentType<StepTypeDetailsProps<AbTestState>>;
  AddExchangeOrderTags: React.ComponentType<
    StepTypeDetailsProps<AddExchangeOrderTagsState>
  >;
  AddReturnTag: React.ComponentType<StepTypeDetailsProps<AddReturnTagState>>;
  DynamicBundleAdjustment: React.ComponentType<
    StepTypeDetailsProps<DynamicBundleAdjustmentState>
  >;
  AiReturnReasons: React.ComponentType<
    StepTypeDetailsProps<AiReturnReasonsState>
  >;
  Condition: React.ComponentType<StepTypeDetailsProps<ConditionState>>;
  MultipleConditions: React.ComponentType<
    StepTypeDetailsProps<MultipleConditionsState>
  >;
  MultipleActions: React.ComponentType<
    StepTypeDetailsProps<MultipleActionsState>
  >;
  SetReturnLocations: React.ComponentType<
    StepTypeDetailsProps<SetReturnLocationsState>
  >;
  Donate: React.ComponentType<StepTypeDetailsProps<DonateState>>;
  CombineLabel: React.ComponentType<StepTypeDetailsProps<CombineLabelState>>;
  SeparateLabels: React.ComponentType<
    StepTypeDetailsProps<SeparateLabelsState>
  >;
  ReturnProcessingDecision: React.ComponentType<
    StepTypeDetailsProps<ReturnProcessingDecisionState>
  >;
  ReturnProcessingInformation: React.ComponentType<
    StepTypeDetailsProps<ReturnProcessingInformationState>
  >;
  Sort: React.ComponentType<StepTypeDetailsProps<SortState>>;
  Evidence: React.ComponentType<StepTypeDetailsProps<EvidenceState>>;
  AddUndeclaredItem: React.ComponentType<
    StepTypeDetailsProps<AddUndeclaredItemState>
  >;
  Verification: React.ComponentType<StepTypeDetailsProps<VerificationState>>;
  ProcessingAction: React.ComponentType<
    StepTypeDetailsProps<ProcessingActionState>
  >;
  VerifyItemVariant: React.ComponentType<
    StepTypeDetailsProps<VerifyItemVariantState>
  >;
  MakeServiceRequest: React.ComponentType<
    StepTypeDetailsProps<MakeServiceRequestState>
  >;
  TransformProduct: React.ComponentType<
    StepTypeDetailsProps<TransformProductState>
  >;
  RedoManagedLocation: React.ComponentType<
    StepTypeDetailsProps<RedoManagedLocationState>
  >;
  Compensation: React.ComponentType<StepTypeDetailsProps<CompensationState>>;
  Restocking: React.ComponentType<StepTypeDetailsProps<RestockingState>>;
  PhaseAnchor: React.ComponentType<StepTypeDetailsProps<PhaseAnchorState>>;
  EndNode: React.ComponentType<StepTypeDetailsProps<EndNodeState>>;
}

export const NullDetails = () => null;
const defaultGetConditionDetails = (condition: Condition) => NullDetails;
const NullRuleConditionDetails: DetailsComponentsForRuleConditions = {
  AfterHoursDetails: NullDetails,
  TicketChannelDetails: NullDetails,
  SubjectLineDetails: NullDetails,
  MessageBodyMatchesDetails: NullDetails,
  TopicDetails: NullDetails,
  ReceivedAtEmailDetails: NullDetails,
  SentFromEmailDetails: NullDetails,
  AssignmentTypeDetails: NullDetails,
};
const NullDetailsComponentsForActions: DetailsComponentsForActions = {
  SetStatusDetails: NullDetails,
  AddTagDetails: NullDetails,
  AutoReplyDetails: NullDetails,
  AssignDetails: NullDetails,
};

export const NullDetailsForSteps: DetailsComponentsForSteps = {
  Start: NullDetails,
  MultipleChoice: NullDetails,
  Flag: NullDetails,
  ManualReview: NullDetails,
  MerchantReviewBeforeFiling: NullDetails,
  DoNothing: NullDetails,
  SubmitWarrantyRegistration: NullDetails,
  Input: NullDetails,
  Information: NullDetails,
  ExchangeShippingName: NullDetails,
  ExchangeShippingRates: NullDetails,
  DiscountCodeName: NullDetails,
  Reject: NullDetails,
  OverrideAdjustment: NullDetails,
  OverrideGreenReturn: NullDetails,
  Return: NullDetails,
  FinalSaleReturn: NullDetails,
  SubmitClaim: NullDetails,
  SubmitWarranty: NullDetails,
  SubmitRepair: NullDetails,
  Block: NullDetails,
  ChatMultipleChoice: NullDetails,
  CloseChat: NullDetails,
  CustomerResponse: NullDetails,
  AssignToAgent: NullDetails,
  SendMessage: NullDetails,
  Trigger: NullDetails,
  Wait: NullDetails,
  SendEmail: NullDetails,
  SendSms: NullDetails,
  SeeOrders: NullDetails,
  AddConversationTag: NullDetails,
  Reentry: NullDetails,
  AbTest: NullDetails,
  AddExchangeOrderTags: NullDetails,
  AddReturnTag: NullDetails,
  DynamicBundleAdjustment: NullDetails,
  AiReturnReasons: NullDetails,
  Condition: NullDetails,
  MultipleConditions: NullDetails,
  MultipleActions: NullDetails,
  SetReturnLocations: NullDetails,
  Donate: NullDetails,
  CombineLabel: NullDetails,
  SeparateLabels: NullDetails,
  ReturnProcessingDecision: NullDetails,
  ReturnProcessingInformation: NullDetails,
  Sort: NullDetails,
  Evidence: NullDetails,
  AddUndeclaredItem: NullDetails,
  Verification: NullDetails,
  ProcessingAction: NullDetails,
  VerifyItemVariant: NullDetails,
  MakeServiceRequest: NullDetails,
  TransformProduct: NullDetails,
  RedoManagedLocation: NullDetails,
  Compensation: NullDetails,
  Restocking: NullDetails,
  PhaseAnchor: NullDetails,
  EndNode: NullDetails,
};

export const nullModelToStepsDetailsProps = {
  getDetailsForCondition: defaultGetConditionDetails,
  DetailsForRuleConditions: NullRuleConditionDetails,
  DetailsForActions: NullDetailsComponentsForActions,
  DetailsForSteps: NullDetailsForSteps,
};

export function modelToSteps({
  model,
  DetailsForSteps,
  getDetailsForCondition,
  DetailsForRuleConditions,
  DetailsForActions,
  firstStepTitle,
  returnReasonTitle,
  flowType,
  startIcon,
}: {
  model: ExtendedReturnFlow;
  DetailsForSteps: DetailsComponentsForSteps;
  getDetailsForCondition: (
    condition: Condition,
  ) => React.ComponentType<ConditionTypeDetailsProps<any>>;
  DetailsForRuleConditions: DetailsComponentsForRuleConditions;
  DetailsForActions: DetailsComponentsForActions;
  firstStepTitle?: string;
  returnReasonTitle?: string;
  flowType?: FlowType;
  startIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}): Map<StepId, Step> {
  // start must come first, to ensure that stepsToModel places that item first as well
  const startState: { next: StepId | undefined } = { next: undefined };
  const result = new Map<StepId, Step>(
    firstStepTitle
      ? [
          [
            "start",
            new TypedStep(
              getStartStep(firstStepTitle, DetailsForSteps.Start, startIcon),
              startState,
            ),
          ],
        ]
      : [],
  );
  for (const [index, step] of model.steps.entries()) {
    let type: StepType<any, any>;
    switch (step.type) {
      case COMPENSATION_STEP:
        type = COMPENSATION(DetailsForSteps.Compensation);
        break;
      case RESTOCKING_STEP:
        type = RESTOCKING(DetailsForSteps.Restocking);
        break;
      case PHASE_ANCHOR_STEP:
        type = PHASE_ANCHOR(DetailsForSteps.PhaseAnchor);
        break;
      case END_NODE_STEP:
        type = END_NODE(DetailsForSteps.EndNode);
        break;
      case ModelStep.CONDITION:
        type = CONDITION(DetailsForSteps.Condition, getDetailsForCondition);
        break;
      case ModelStep.INPUT:
        type =
          step.parts[0]?.fieldId === FieldId.WARRANTIES_ORDER_DATE
            ? ORDER_DATE_INPUT(DetailsForSteps.Input)
            : INPUT(DetailsForSteps.Input);
        break;
      case ModelStep.INFORMATION:
        type = INFORMATION(DetailsForSteps.Information);
        break;
      case ModelStep.MANUAL_REVIEW:
        type = MANUAL_REVIEW(DetailsForSteps.ManualReview);
        break;
      case ModelStep.MERCHANT_REVIEW_BEFORE_FILING:
        type = MERCHANT_REVIEW_BEFORE_FILING(
          DetailsForSteps.MerchantReviewBeforeFiling,
        );
        break;
      case ModelStep.FLAG:
        type = FLAG(DetailsForSteps.Flag);
        break;
      case ModelStep.EXCHANGE_SHIPPING_NAME:
        type = EXCHANGE_SHIPPING_NAME(DetailsForSteps.ExchangeShippingName);
        break;
      case ModelStep.EXCHANGE_SHIPPING_RATES:
        type = EXCHANGE_SHIPPING_RATES(DetailsForSteps.ExchangeShippingRates);
        break;
      case ModelStep.DISCOUNT_CODE_NAME:
        type = DISCOUNT_CODE_NAME(DetailsForSteps.DiscountCodeName);
        break;
      case ModelStep.MULTIPLE_CHOICE:
        type = step.reason
          ? getReturnReasonStep(
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              returnReasonTitle!,
              DetailsForSteps.MultipleChoice,
            )
          : MULTIPLE_CHOICE(DetailsForSteps.MultipleChoice);
        break;
      case ModelStep.REJECT:
        type = REJECT(DetailsForSteps.Reject);
        break;
      case ModelStep.OVERRIDE_ADJUSTMENT:
        type = OVERRIDE_ADJUSTMENT(DetailsForSteps.OverrideAdjustment);
        break;
      case ModelStep.OVERRIDE_GREEN_RETURN:
        type = OVERRIDE_GREEN_RETURN(DetailsForSteps.OverrideGreenReturn);
        break;
      case ModelStep.RETURN:
        type = RETURN(DetailsForSteps.Return, returnStepTypeName(flowType));
        break;
      case ModelStep.FINAL_SALE_RETURN:
        type = FINAL_SALE_RETURN(DetailsForSteps.FinalSaleReturn);
        break;
      case ModelStep.SUBMIT_CLAIM:
        type = SUBMIT_CLAIM(DetailsForSteps.SubmitClaim);
        break;
      case ModelStep.SUBMIT_WARRANTY:
        type = SUBMIT_WARRANTY(DetailsForSteps.SubmitWarranty);
        break;
      case ModelStep.SUBMIT_REPAIR:
        type = SUBMIT_REPAIR(DetailsForSteps.SubmitRepair);
        break;
      case ModelStep.BLOCK:
        type = BLOCK(DetailsForSteps.Block);
        break;
      case ModelStep.CHAT_MULTIPLE_CHOICE:
        type = CHAT_MULTIPLE_CHOICE(DetailsForSteps.ChatMultipleChoice);
        break;
      case ModelStep.CLOSE_CHAT:
        type = CLOSE_CHAT(DetailsForSteps.CloseChat);
        break;
      case ModelStep.CUSTOMER_RESPONSE:
        type = CUSTOMER_RESPONSE(DetailsForSteps.CustomerResponse);
        break;
      case ModelStep.ASSIGN_TO_AGENT:
        type = ASSIGN_TO_AGENT(DetailsForSteps.AssignToAgent);
        break;
      case ModelStep.SEND_MESSAGE:
        type = SEND_MESSAGE(DetailsForSteps.SendMessage);
        break;
      case ModelStep.REENTRY:
        type = REENTRY(DetailsForSteps.Reentry);
        break;
      case ModelStep.TRIGGER:
        type = TRIGGER(DetailsForSteps.Trigger);
        break;
      case ModelStep.WAIT:
        type = WAIT(DetailsForSteps.Wait);
        break;
      case ModelStep.SEND_EMAIL:
        type = SEND_EMAIL(DetailsForSteps.SendEmail);
        break;
      case ModelStep.SEND_SMS:
        type = SEND_SMS(DetailsForSteps.SendSms);
        break;
      case ModelStep.SEE_ORDERS:
        type = SEE_ORDERS(DetailsForSteps.SeeOrders);
        break;
      case ModelStep.ADD_CONVERSATION_TAG:
        type = ADD_CONVERSATION_TAG(DetailsForSteps.AddConversationTag);
        break;
      case ModelStep.MULTIPLE_CONDITIONS:
        type = MULTIPLE_CONDITIONS(
          DetailsForSteps.MultipleConditions,
          DetailsForRuleConditions,
        );
        break;
      case ModelStep.MULTIPLE_ACTIONS:
      // fallthrough
      case ModelStep.ACTION:
        type = MULTIPLE_ACTIONS(
          DetailsForSteps.MultipleActions,
          DetailsForActions,
        );
        break;
      case ModelStep.AB_TEST:
        type = AB_TEST(DetailsForSteps.AbTest);
        break;
      case ModelStep.DO_NOTHING:
        type = DO_NOTHING(DetailsForSteps.DoNothing);
        break;
      case ModelStep.SUBMIT_WARRANTY_REGISTRATION:
        type = SUBMIT_WARRANTY_REGISTRATION(
          DetailsForSteps.SubmitWarrantyRegistration,
        );
        break;
      case ModelStep.EW_UPSELL:
        throw new Error("EW_UPSELL is not supported in the flow editor");
      case ModelStep.AI_RETURN_REASONS:
        type = AI_RETURN_REASONS(
          DetailsForSteps.AiReturnReasons,
          aiReturnReasonsStepOptions(flowType),
        );
        break;
      case ModelStep.ADD_RETURN_TAG: {
        const tagTypeName = getTagTypeName(flowType);
        const addReturnTagTitle = `Add ${tagTypeName.toLowerCase()} tag`;
        type = ADD_RETURN_TAG(DetailsForSteps.AddReturnTag, addReturnTagTitle);
        break;
      }
      case ModelStep.ADD_EXCHANGE_ORDER_TAGS:
        type = ADD_EXCHANGE_ORDER_TAGS(DetailsForSteps.AddExchangeOrderTags);
        break;
      case ModelStep.DYNAMIC_BUNDLE_ADJUSTMENT:
        type = DYNAMIC_BUNDLE_ADJUSTMENT(
          DetailsForSteps.DynamicBundleAdjustment,
        );
        break;
      case ModelStep.SET_RETURN_LOCATIONS:
        if (step.useDonation) {
          type = DONATE(DetailsForSteps.Donate);
        } else {
          type = SET_RETURN_LOCATIONS(DetailsForSteps.SetReturnLocations);
        }
        break;
      case ModelStep.COMBINE_LABEL:
        type = COMBINE_LABEL(DetailsForSteps.CombineLabel);
        break;
      case ModelStep.SEPARATE_LABELS:
        type = SEPARATE_LABELS(DetailsForSteps.SeparateLabels);
        break;
      case ModelStep.RETURN_PROCESSING_DECISION:
        type = RETURN_PROCESSING_DECISION(
          DetailsForSteps.ReturnProcessingDecision,
        );
        break;
      case ModelStep.RETURN_PROCESSING_INFORMATION:
        type = RETURN_PROCESSING_INFORMATION(
          DetailsForSteps.ReturnProcessingInformation,
        );
        break;
      case ModelStep.SORT:
        type = SORT(DetailsForSteps.Sort);
        break;
      case ModelStep.EVIDENCE:
        type = EVIDENCE(DetailsForSteps.Evidence);
        break;
      case ModelStep.ADD_UNDECLARED_ITEM:
        type = ADD_UNDECLARED_ITEM(DetailsForSteps.AddUndeclaredItem);
        break;
      case ModelStep.VERIFICATION:
        type = VERIFICATION(DetailsForSteps.Verification);
        break;
      case ModelStep.PROCESSING_ACTION:
        type = PROCESSING_ACTION(DetailsForSteps.ProcessingAction);
        break;
      case ModelStep.VERIFY_ITEM_VARIANT:
        type = VERIFY_ITEM_VARIANT(DetailsForSteps.VerifyItemVariant);
        break;
      case ModelStep.MAKE_SERVICE_REQUEST:
        type = MAKE_SERVICE_REQUEST(DetailsForSteps.MakeServiceRequest);
        break;
      case ModelStep.TRANSFORM_PRODUCT:
        type = TRANSFORM_PRODUCT(DetailsForSteps.TransformProduct);
        break;
      case ModelStep.REDO_MANAGED_LOCATION:
        type = REDO_MANAGED_LOCATION(DetailsForSteps.RedoManagedLocation);
        break;
      default:
        invalidStepType(step);
    }
    const id = String(index);
    if (startState.next === undefined) {
      startState.next = id;
    }
    result.set(id, new TypedStep(type, type.fromModel(step, String)));
  }
  return result;
}

/**
 * Narrow an `ExtendedReturnFlow` to a plain `ReturnFlow` for editors that
 * never produce Compensation/Restocking steps (the legacy and standard
 * ReturnFlow editors). Throws if any extended-only step types are present —
 * those should only ever come out of the AdvancedFlow editor, which keeps the
 * extended shape.
 */
export function narrowToReturnFlow(flow: ExtendedReturnFlow): ReturnFlow {
  const narrowedSteps: ModelStep[] = [];
  for (const step of flow.steps) {
    if (
      step.type === COMPENSATION_STEP ||
      step.type === RESTOCKING_STEP ||
      step.type === PHASE_ANCHOR_STEP ||
      step.type === END_NODE_STEP
    ) {
      throw new Error(
        `Step type "${String(step.type)}" is only supported in the AdvancedFlow editor`,
      );
    }
    narrowedSteps.push(step);
  }
  return { ...flow, steps: narrowedSteps };
}

export function stepsToModel(
  steps: Map<StepId, Step>,
  options?: {
    defaultResetStepId?: StepId;
    redoManagedMeta?: Map<StepId, RedoManagedStepMeta>;
  },
): ExtendedReturnFlow {
  const redoManagedMeta = options?.redoManagedMeta;
  const stepFns = new Map<StepId, StepFn>();
  for (const [id, step] of steps) {
    if (step.start && step.ignore()) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      stepFns.set(step.start, undefined!); // ensure first step is first
      continue;
    }
    stepFns.set(id, (idFn) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unsafe-type-assertion
      const model = step.toModel((id) => idFn(stepFns.get(id)!)) as ModelStep;
      const meta = redoManagedMeta?.get(id);
      if (meta) {
        model.redoManaged = true;
        if (meta.editableFields?.length) {
          model.redoManagedEditableFields = meta.editableFields;
        }
        if (meta.editableHandles?.length) {
          model.redoManagedEditableHandles = meta.editableHandles;
        }
      }
      return model;
    });
  }

  const { steps: newSteps, idByStepFn } = stepsBuildWithMapping(
    stepFns.values(),
  );

  let defaultResetStep: number | undefined;
  if (options?.defaultResetStepId != null) {
    const fn = stepFns.get(options.defaultResetStepId);
    if (fn) {
      defaultResetStep = idByStepFn.get(fn);
    }
  }

  return { steps: newSteps, defaultResetStep, type: FlowType.RETURN };
}
